import { useEffect } from 'react';
import { fitTitle, fitDescription } from '../utils/seoFormat';
import { legacyContentFor } from '../data/legacyContent';

const SEO = ({ title, description, path, url, type = 'website', image, jsonLd, keywords, alternates, geoRegion = 'US', geoPlacename = 'United States', lang = 'en', noindex = false }) => {
  const baseUrl = 'https://landdrafters.com';
  const finalPath = path || url || '';
  // Title 50–60 chars and description 120–155 chars enforced centrally (Google SEO Starter Guide)
  const fullTitle = fitTitle(typeof title === 'string' && title ? title : 'Land Survey CAD Drafting Services Provider');
  const desc = fitDescription(legacyContentFor(finalPath)?.description || description || 'Land Drafters LLC — leading CAD drafting services provider for outsourcing CAD drafting, land survey CAD drafting, BIM modeling, Revit, Civil 3D, ALTA surveys, architectural drafting, and 3D modeling across all 50 US states. Fast turnaround, survey-grade accuracy.');
  const ogImage = image || `${baseUrl}/og-image.png`;
  const kws = keywords || 'CAD Drafting Services Provider, Outsourcing CAD Drafting, Land Survey CAD Drafting Services Provider, AutoCAD drafter, Civil 3D drafting, BIM modeling, Revit outsourcing, ALTA survey CAD, ALTA survey drafting, boundary survey drafting, topographic survey, topographic mapping, architectural drafting USA, steel detailing outsourcing, 3D modeling services, land surveying CAD, parcel mapping, oil & gas drafting, plat preparation';

  useEffect(() => {
    document.title = fullTitle;
    
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (el) {
        el.setAttribute('content', content);
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.content = content;
        document.head.appendChild(el);
      }
    };

    // Standard meta
    setMeta('name', 'description', desc);
    document.querySelector('meta[name="keywords"]')?.remove();
    setMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');
    setMeta('name', 'googlebot', noindex ? 'noindex, follow' : 'index, follow');
    setMeta('name', 'author', 'Land Drafters LLC');
    setMeta('name', 'geo.region', geoRegion);
    setMeta('name', 'geo.placename', geoPlacename);
    document.documentElement.lang = lang;

    // hreflang alternates
    document.querySelectorAll('link[data-hreflang]').forEach((el) => el.remove());
    (alternates || []).forEach((a) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = a.hreflang;
      link.href = `${baseUrl}${a.path}`;
      link.setAttribute('data-hreflang', '1');
      document.head.appendChild(link);
    });

    // Open Graph
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:site_name', 'Land Drafters LLC');
    if (finalPath) setMeta('property', 'og:url', `${baseUrl}${finalPath}`);

    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', desc);
    setMeta('name', 'twitter:image', ogImage);

    // Canonical
    if (finalPath) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', `${baseUrl}${finalPath}`);
      } else {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        canonical.href = `${baseUrl}${finalPath}`;
        document.head.appendChild(canonical);
      }
    }

    // JSON-LD Structured Data
    const ldId = 'ld-json-seo';
    let ldScript = document.getElementById(ldId);
    const structuredData = jsonLd || {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "Land Drafters LLC",
      "alternateName": ["Land Drafters", "Land Drafters CAD Services"],
      "description": desc,
      "url": baseUrl,
      "telephone": "+1-786-480-8238",
      "email": "sg@landdrafters.com",
      "image": ogImage,
      "logo": `${baseUrl}/logo.png`,
      "priceRange": "$$",
      "openingHours": "Mo-Sa 07:00-17:00",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
          "opens": "07:00",
          "closes": "17:00"
        }
      ],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "#916, 4530 S Orange Blossom Trl",
        "addressLocality": "Orlando",
        "addressRegion": "FL",
        "postalCode": "32839",
        "addressCountry": "US"
      },
      "geo": { "@type": "GeoCoordinates", "latitude": 28.4735, "longitude": -81.3966 },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+1-786-480-8238",
          "contactType": "customer service",
          "email": "sg@landdrafters.com",
          "areaServed": ["US", "CA", "GB", "AU"],
          "availableLanguage": ["English"]
        },
        {
          "@type": "ContactPoint",
          "telephone": "+91-99602-50279",
          "contactType": "technical support",
          "areaServed": ["US", "CA", "GB", "AU", "IN"],
          "availableLanguage": ["English", "Hindi"]
        },
        {
          "@type": "ContactPoint",
          "contactType": "sales",
          "email": "landdrafters@gmail.com",
          "areaServed": "US",
          "availableLanguage": ["English"]
        }
      ],
      "sameAs": [
        "https://www.linkedin.com/company/land-drafters-llc",
        "https://www.facebook.com/landdrafters",
        "https://t.me/CADPartner"
      ],
      "areaServed": [
        { "@type": "Country", "name": "United States" },
        { "@type": "AdministrativeArea", "name": "All 50 US States + DC" }
      ],
      "knowsAbout": [
        "CAD Drafting",
        "Land Surveying",
        "BIM Modeling",
        "AutoCAD",
        "Civil 3D",
        "Revit",
        "ALTA Surveys",
        "Boundary Surveys",
        "Topographic Surveys",
        "Architectural Drafting",
        "Steel Detailing",
        "3D Modeling",
        "Point Cloud to BIM",
        "MEP Drafting"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "CAD & Surveying Services",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Land Surveying CAD Drafting" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Civil Engineering CAD Drafting" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Architectural CAD Drafting" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "BIM Modeling Services" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "3D Modeling & Visualization" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Steel Detailing" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Mechanical CAD Drafting" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Land Development CAD Services" } }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "150"
      }
    };

    if (ldScript) {
      ldScript.textContent = JSON.stringify(structuredData);
    } else {
      ldScript = document.createElement('script');
      ldScript.id = ldId;
      ldScript.type = 'application/ld+json';
      ldScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(ldScript);
    }

    return () => {
      const el = document.getElementById(ldId);
      if (el) el.remove();
      document.querySelectorAll('link[data-hreflang]').forEach((l) => l.remove());
      document.documentElement.lang = 'en';
    };
  }, [fullTitle, desc, finalPath, type, ogImage, jsonLd, baseUrl, kws, alternates, geoRegion, geoPlacename, lang, noindex]);

  return null;
};

export default SEO;
